import { useMemo } from 'react';
import { useKeycloak } from 'src/stores/keycloak';
import { useMagicLink } from 'src/stores/magicLink';
import { getAuthProvider, isAuthDisabled } from './authConfig';
import { useEntraAuth } from './useEntraAuth';
import {
  AuthState,
  AuthProvider as AuthProviderType,
  keycloakTokenToUserInfo,
} from './types';
import { magicLinkLogout } from 'src/services/auth/magicLinkLogout';

/**
 * Create a mock AuthState for when authentication is globally disabled
 */
const createMockAuthState = (): AuthState => ({
  initialized: true,
  authenticated: true,
  user: {
    id: 'mock-user',
    email: 'mock@example.com',
    name: 'Mock User',
    preferred_username: 'mock-user',
    roles: [],
  },
  tokenMinValiditySeconds: 0,
  tokenExpiresAt: undefined, // No token expiration for mock state
  actions: {
    initialize: async () => {
      // No-op for disabled auth
    },
    login: async () => {
      // No-op for disabled auth
    },
    logout: async () => {
      // No-op for disabled auth
    },
    updateToken: async () => true,
    getAccessToken: async () => 'mock-token',
  },
});

/**
 * Unified Authentication Hook
 *
 * This hook provides a consistent authentication interface that works with both
 * Keycloak and Microsoft Entra ID authentication providers. It automatically
 * detects the configured provider and returns the appropriate authentication state.
 *
 * When authentication is globally disabled (VITE_DISABLE_AUTH=true), it returns
 * a mock AuthState that allows the application to function without authentication.
 */
export function useAuth(): AuthState {
  // Always call hooks in the same order (React hooks rules)
  const keycloakState = useKeycloak();
  const entraState = useEntraAuth();
  const magicLinkState = useMagicLink();

  // Always create keycloakAuthState to maintain hook order
  const keycloakAuthState = useMemo((): AuthState => {
    // Extract token expiration from Keycloak ID token
    const tokenExpiresAt = keycloakState.idTokenParsed?.exp
      ? new Date(keycloakState.idTokenParsed.exp * 1000)
      : undefined;

    return {
      initialized: keycloakState.initialized,
      authenticated: keycloakState.authenticated,
      user: keycloakTokenToUserInfo(keycloakState.idTokenParsed),
      tokenMinValiditySeconds: keycloakState.tokenMinValiditySeconds,
      tokenExpiresAt,
      actions: {
        initialize: keycloakState.actions.initialize,
        login: keycloakState.actions.login,
        logout: keycloakState.actions.logout,
        updateToken: keycloakState.actions.updateToken,
        getAccessToken: async () => {
          await keycloakState.actions.updateToken();
          return keycloakState.actions.getToken();
        },
      },
    };
  }, [
    keycloakState.initialized,
    keycloakState.authenticated,
    keycloakState.idTokenParsed,
    keycloakState.tokenMinValiditySeconds,
    keycloakState.actions,
  ]);

  // Create magic link auth state
  const magicLinkAuthState = useMemo((): AuthState => {
    // If we have tokens and user, consider authenticated (even if flag isn't set due to rehydration)
    const hasValidTokens =
      magicLinkState.accessToken &&
      magicLinkState.user &&
      (!magicLinkState.tokenExpiresAt ||
        magicLinkState.tokenExpiresAt > new Date());

    return {
      initialized: magicLinkState.initialized,
      authenticated: hasValidTokens || magicLinkState.authenticated,
      user: magicLinkState.user,
      tokenMinValiditySeconds: 30, // Default minimum validity
      tokenExpiresAt: magicLinkState.tokenExpiresAt,
      actions: {
        initialize: magicLinkState.actions.initialize,
        login: async () => {
          // Login for magic link happens via the callback URL, not through this action
          // This is a no-op to prevent errors when AuthProtectedRoute tries to trigger login
          // If tokens are present, they will be detected automatically
          // If no tokens, user should use the magic link from email
          const currentState = magicLinkState;
          if (!currentState.authenticated && !currentState.accessToken) {
            // No tokens - this is expected, user needs to use magic link
            // Don't throw error, just return silently
            return;
          }
        },
        logout: async () => {
          const accessToken = magicLinkState.actions.getAccessToken();
          if (accessToken) {
            try {
              await magicLinkLogout(accessToken);
            } catch (error) {
              // Even if logout API fails, clear local state
              console.error('Logout API call failed:', error);
            }
          }
          magicLinkState.actions.clearTokens();
        },
        updateToken: magicLinkState.actions.updateToken,
        getAccessToken: async () => {
          return magicLinkState.actions.getAccessToken();
        },
      },
    };
  }, [magicLinkState]);

  // Check if authentication is globally disabled
  const authDisabled = isAuthDisabled();

  // Return mock state if authentication is globally disabled
  if (authDisabled) {
    return createMockAuthState();
  }

  // Priority 1: Check if user is authenticated via magic link (regardless of AUTH_PROVIDER)
  // This works like Entra - automatically detected when tokens are present
  // Check for tokens - magicLinkAuthState already handles validation
  if (magicLinkState.accessToken && magicLinkState.user) {
    return magicLinkAuthState;
  }

  // Priority 2: Use the configured AUTH_PROVIDER
  const authProvider = getAuthProvider();

  if (authProvider === 'entra') {
    return entraState;
  }

  if (authProvider === 'keycloak') {
    return keycloakAuthState;
  }

  if (authProvider === 'magic-link') {
    return magicLinkAuthState;
  }

  // This should never happen due to validation in getAuthProvider()
  throw new Error(`Unsupported authentication provider: ${authProvider}`);
}

/**
 * Hook to get the current authentication provider type
 * Useful for provider-specific logic or debugging
 *
 * @returns The current authentication provider ("keycloak" | "entra" | "magic-link")
 */
export function useAuthProvider(): AuthProviderType {
  return useMemo(() => getAuthProvider(), []);
}

/**
 * Hook to check if a specific authentication provider is active
 *
 * @param provider - The provider to check for
 * @returns True if the specified provider is active
 */
export function useIsAuthProvider(provider: AuthProviderType): boolean {
  const currentProvider = useAuthProvider();
  return currentProvider === provider;
}

export default useAuth;
